// lib/playbook-schemas.js — CANONICAL PLAYBOOK SCHEMAS
// ═══════════════════════════════════════════════════════════
// Single source of truth for the content shape of each playbook
// subtype. Shift reads this on the server to structure the user's
// input; the client reads it to render the live preview card.
//
// Principle: the user's words stay the user's words. Shift cleans
// grammar, splits into fields, classifies the type — but never
// invents content the user didn't say.
//
// The 36 subtypes in hooks/use-playbook-items.js collapse into
// these ~10 canonical shapes. Subtypes that share a shape share a
// schema (Process / SOP / Workflow / Sequence all use "steps").
// ═══════════════════════════════════════════════════════════

// Maps each subtype → { shape: 'schemaKey', rationale: 'one-line hint' }
export const SUBTYPE_REGISTRY = {
  // ── LENSES ─────────────────────────────
  mental_model:    { shape: 'mental_model',  rationale: 'A way of seeing. Concept + how it applies.' },
  framework:       { shape: 'framework',     rationale: 'A repeatable structure. Phases or dimensions.' },
  heuristic:       { shape: 'heuristic',     rationale: 'A rule of thumb. Fast shortcut, not full analysis.' },
  principle:       { shape: 'principle',     rationale: 'A belief you hold, and when to break it.' },
  decision_matrix: { shape: 'rubric',        rationale: 'Weighted criteria for picking among options.' },
  snippet:         { shape: 'snippet',       rationale: 'A saved chunk — any reusable text.' },
  prompt:          { shape: 'prompt',        rationale: 'A reusable prompt with use case + example.' },

  // ── PLAYS ──────────────────────────────
  template:        { shape: 'template',      rationale: 'A fill-in structure for a recurring output.' },
  process:         { shape: 'steps',         rationale: 'Steps you run every time.' },
  sop:             { shape: 'steps',         rationale: 'Standard procedure for a repeated task.' },
  workflow:        { shape: 'steps',         rationale: 'How work moves from start to finish.' },
  sequence:        { shape: 'steps',         rationale: 'An ordered series of actions.' },
  checklist:       { shape: 'checklist',     rationale: 'Items to verify — not necessarily in order.' },
  routine:         { shape: 'steps',         rationale: 'A regular practice you repeat.' },
  algorithm:       { shape: 'steps',         rationale: 'A precise step-by-step computation.' },
  pipeline:        { shape: 'steps',         rationale: 'Stages data/work flows through.' },
  protocol:        { shape: 'steps',         rationale: 'Agreed-upon procedure, often with others.' },
  method:          { shape: 'steps',         rationale: 'An approach, broken into its parts.' },
  function:        { shape: 'steps',         rationale: 'A scoped routine with a clear output.' },
  lifecycle:       { shape: 'steps',         rationale: 'Stages of something from birth to end.' },

  // ── READS ──────────────────────────────
  cheatsheet:      { shape: 'cheatsheet',    rationale: 'Quick-reference rows, key:value.' },
  diagnostic:      { shape: 'diagnostic',    rationale: 'Questions that narrow down what\'s wrong.' },
  rubric:          { shape: 'rubric',        rationale: 'Scoring criteria with weights.' },
  benchmark:       { shape: 'rubric',        rationale: 'Standards to measure against.' },
  anti_pattern:    { shape: 'principle',     rationale: 'What not to do, and why.' },
  trigger:         { shape: 'heuristic',     rationale: 'Signal that means "act now".' },
  exit_criteria:   { shape: 'checklist',     rationale: 'How you know it\'s truly done.' },
  scenario:        { shape: 'snippet',       rationale: 'A specific situation worth keeping.' },
  calibration:     { shape: 'rubric',        rationale: 'How to tune judgment in this area.' },

  // ── FRAMES ─────────────────────────────
  structure:       { shape: 'structure',     rationale: 'A layout for organizing information.' },
  taxonomy:        { shape: 'taxonomy',      rationale: 'A way to classify and group things.' },
  layout:          { shape: 'structure',     rationale: 'Spatial arrangement of parts.' },
  constraint_set:  { shape: 'principle',     rationale: 'The rules of the box you\'re working in.' },
  flowchart:       { shape: 'steps',         rationale: 'Branching paths through decisions.' },
  formula:         { shape: 'snippet',       rationale: 'A formula, equation, or exact form.' },
  blueprint:       { shape: 'structure',     rationale: 'A system design — how parts connect.' },
};

// The canonical shapes. Each defines what fields a stored item has
// and how to render them as summary bullets.
export const SHAPES = {
  // Minimal — just text (catchall for snippets, formulas, scenarios)
  snippet: {
    fields: ['text'],
    requiredFields: ['text'],
    bulletKeys: ['text'],
  },

  // Steps — Process, SOP, Workflow, Sequence, Routine, etc.
  steps: {
    fields: ['purpose', 'steps'],
    requiredFields: ['steps'],
    // steps is an array of { description } objects
    bulletKeys: ['purpose'],
  },

  // Checklist — Exit Criteria
  checklist: {
    fields: ['purpose', 'items'],
    requiredFields: ['items'],
    // items is an array of { check, note? }
    bulletKeys: ['purpose'],
  },

  // Template — fillable structure
  template: {
    fields: ['purpose', 'blocks'],
    requiredFields: ['blocks'],
    // blocks is an array of { heading, body }
    bulletKeys: ['purpose'],
  },

  // Mental Model
  mental_model: {
    fields: ['core_concept', 'how_it_applies', 'example'],
    requiredFields: ['core_concept'],
    bulletKeys: ['core_concept', 'example'],
  },

  // Framework
  framework: {
    fields: ['purpose', 'phases'],
    requiredFields: ['phases'],
    // phases is an array of { name, description }
    bulletKeys: ['purpose'],
  },

  // Heuristic / Trigger
  heuristic: {
    fields: ['rule', 'when_to_use', 'counter_case'],
    requiredFields: ['rule'],
    bulletKeys: ['rule', 'when_to_use'],
  },

  // Principle / Anti-Pattern / Constraint
  principle: {
    fields: ['statement', 'why_it_matters', 'when_to_break_it'],
    requiredFields: ['statement'],
    bulletKeys: ['statement', 'why_it_matters'],
  },

  // Prompt
  prompt: {
    fields: ['prompt_text', 'use_case', 'example_output'],
    requiredFields: ['prompt_text'],
    bulletKeys: ['use_case'],
  },

  // Rubric / Benchmark / Decision Matrix / Calibration
  rubric: {
    fields: ['what_scored', 'criteria'],
    requiredFields: ['criteria'],
    // criteria is array of { name, weight?, description }
    bulletKeys: ['what_scored'],
  },

  // Diagnostic — set of questions
  diagnostic: {
    fields: ['purpose', 'questions'],
    requiredFields: ['questions'],
    // questions is array of strings
    bulletKeys: ['purpose'],
  },

  // Cheatsheet — key/value sections
  cheatsheet: {
    fields: ['purpose', 'sections'],
    requiredFields: ['sections'],
    // sections is array of { heading, rows: [{ key, value }] }
    bulletKeys: ['purpose'],
  },

  // Structure / Layout / Blueprint
  structure: {
    fields: ['purpose', 'parts'],
    requiredFields: ['parts'],
    // parts is array of { name, role? }
    bulletKeys: ['purpose'],
  },

  // Taxonomy
  taxonomy: {
    fields: ['purpose', 'categories'],
    requiredFields: ['categories'],
    // categories is array of { name, definition, examples? }
    bulletKeys: ['purpose'],
  },
};

// Given a subtype, return its canonical shape definition
export function shapeForSubtype(itemType) {
  const entry = SUBTYPE_REGISTRY[itemType];
  if (!entry) return SHAPES.snippet;
  return SHAPES[entry.shape] || SHAPES.snippet;
}

export function rationaleForSubtype(itemType) {
  return SUBTYPE_REGISTRY[itemType]?.rationale || '';
}

// Starter chips shown above the chat input on the opening screen.
// Each chip prefills the user's input with a half-sentence so they
// just complete the thought. The chips teach the taxonomy without
// naming it.
export const STARTER_CHIPS = [
  { label: 'A process I follow when…',               seed: 'A process I follow when ' },
  { label: 'A mental model for thinking about…',     seed: 'A mental model for thinking about ' },
  { label: 'A template I reuse for…',                seed: 'A template I reuse for ' },
  { label: 'A rule of thumb about…',                 seed: 'A rule of thumb about ' },
  { label: 'A question I ask when deciding…',        seed: 'A question I ask when deciding ' },
  { label: 'From scratch — I\'ll describe it',       seed: '' },
];

// For rendering summary bullets from a stored playbook item —
// used by the Playbook card grid and the preview during creation.
// Returns up to `max` short strings.
export function extractBullets(item, max = 3) {
  const itemType = item?.item_type;
  const shape = shapeForSubtype(itemType);
  const c = item?.content || {};
  const out = [];

  // Primary: use the shape's bulletKeys to pull declared fields
  for (const key of shape.bulletKeys || []) {
    const v = c[key];
    if (v && typeof v === 'string' && v.trim()) {
      out.push(v.trim());
      if (out.length >= max) return out;
    }
  }

  // Secondary: pull from array fields that render as multi-line
  if (Array.isArray(c.steps)) {
    c.steps.slice(0, max - out.length).forEach(s => {
      const text = s?.description || s?.text || s;
      if (typeof text === 'string' && text.trim()) out.push(text.trim());
    });
  } else if (Array.isArray(c.items)) {
    c.items.slice(0, max - out.length).forEach(i => {
      const text = i?.check || i?.text || i;
      if (typeof text === 'string' && text.trim()) out.push(text.trim());
    });
  } else if (Array.isArray(c.criteria)) {
    c.criteria.slice(0, max - out.length).forEach(cr => {
      const name = cr?.name || '';
      const desc = cr?.description || '';
      const text = [name, desc].filter(Boolean).join(' — ');
      if (text) out.push(text);
    });
  } else if (Array.isArray(c.phases)) {
    c.phases.slice(0, max - out.length).forEach(p => {
      const name = p?.name || '';
      const desc = p?.description || '';
      const text = [name, desc].filter(Boolean).join(' — ');
      if (text) out.push(text);
    });
  } else if (Array.isArray(c.questions)) {
    c.questions.slice(0, max - out.length).forEach(q => {
      if (typeof q === 'string' && q.trim()) out.push(q.trim());
    });
  } else if (Array.isArray(c.blocks)) {
    c.blocks.slice(0, max - out.length).forEach(b => {
      if (b?.heading) out.push(b.heading);
    });
  } else if (Array.isArray(c.parts)) {
    c.parts.slice(0, max - out.length).forEach(p => {
      if (p?.name) out.push(p.name);
    });
  } else if (Array.isArray(c.categories)) {
    c.categories.slice(0, max - out.length).forEach(cat => {
      if (cat?.name) out.push(cat.name);
    });
  }

  return out.slice(0, max);
}
